import React, { useState, useEffect } from 'react';
import { MapPin, Trash2, Home, CheckCircle2, Plus, Edit2, X } from 'lucide-react';
import { Link } from 'react-router-dom';

function SavedAddresses({ accessToken }) {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal & Form States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState(null); 
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [formData, setFormData] = useState({
    phone_number: "",
    address_line_1: "",
    address_line_2: "",
    city: "",
    state: "",
    pincode: "",
    is_default: false
  });

  const fetchAddresses = () => {
    fetch(`${process.env.REACT_APP_API_URL}/api/products/addresses/`, {
      headers: { 'Authorization': `Bearer ${accessToken}` }
    })
      .then(res => res.json())
      .then(data => {
        setAddresses(Array.isArray(data) ? data : (data.results || []));
        setLoading(false);
      })
      .catch(err => {
        console.error("Error loading addresses:", err);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (accessToken) fetchAddresses();
  }, [accessToken]);

  // Real-time Pincode Lookup inside Modal
  useEffect(() => {
    const pincode = formData.pincode.trim();
    if (/^\d{6}$/.test(pincode)) {
      setPincodeLoading(true);
      fetch(`https://api.postalpincode.in/pincode/${pincode}`)
        .then(res => res.json())
        .then(data => {
          if (data && data[0]?.Status === "Success") {
            const details = data[0].PostOffice[0];
            setFormData(prev => ({
              ...prev,
              city: details.District,
              state: details.State
            }));
          }
        })
        .catch(err => console.error("Pincode API Error:", err))
        .finally(() => setPincodeLoading(false));
    }
  }, [formData.pincode]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const openAddModal = () => {
    setEditingAddressId(null);
    setFormData({
      phone_number: "",
      address_line_1: "",
      address_line_2: "",
      city: "",
      state: "",
      pincode: "",
      is_default: false
    });
    setIsModalOpen(true);
  };

  const openEditModal = (addr) => {
    setEditingAddressId(addr.id);
    setFormData({
      phone_number: addr.phone_number,
      address_line_1: addr.address_line_1,
      address_line_2: addr.address_line_2 || "",
      city: addr.city,
      state: addr.state,
      pincode: addr.pincode,
      is_default: addr.is_default
    });
    setIsModalOpen(true);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    
    const isEditing = editingAddressId !== null;
    const url = isEditing 
      ? `${process.env.REACT_APP_API_URL}/api/products/addresses/${editingAddressId}/`
      : `${process.env.REACT_APP_API_URL}/api/products/addresses/`;
    
    const method = isEditing ? 'PUT' : 'POST';

    fetch(url, {
      method: method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      },
      body: JSON.stringify(formData)
    })
      .then(res => {
        if (res.ok) {
          setIsModalOpen(false);
          fetchAddresses();
        } else {
          alert("Failed to save address data.");
        }
      })
      .catch(err => console.error("Error saving address details:", err));
  };

  const handleDelete = (id) => {
    if (!window.confirm("Are you sure you want to delete this address?")) return;

    fetch(`${process.env.REACT_APP_API_URL}/api/products/addresses/${id}/`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${accessToken}` }
    })
      .then(res => {
        if (res.ok) {
          setAddresses(prev => prev.filter(addr => addr.id !== id));
        } else {
          alert("Failed to delete address.");
        }
      })
      .catch(err => console.error("Error deleting address:", err));
  };

  const handleSetDefault = (id) => {
    fetch(`${process.env.REACT_APP_API_URL}/api/products/addresses/${id}/`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      },
      body: JSON.stringify({ is_default: true })
    })
      .then(res => {
        if (res.ok) fetchAddresses();
      })
      .catch(err => console.error("Error updating default status:", err));
  };

  if (loading) {
    return (
      <div className="text-center py-32 text-[#A6A29A] uppercase tracking-widest text-[11px] font-semibold bg-[#F2F1EC] min-h-screen flex items-center justify-center">
        Syncing Address Portfolio...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F2F1EC] py-6 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        
        {/* Navigation Breadcrumb Headers */}
        <div className="p-6 bg-[#FDFDFB] border border-[#E1DFD7] rounded-md shadow-xs flex flex-col sm:flex-row sm:items-baseline justify-between gap-4">
          <div className="flex items-center gap-3">
            <MapPin className="w-4 h-4 text-[#9A7B56]" />
            <div>
              <h1 className="text-[#2C2A27] text-sm uppercase tracking-widest font-semibold text-[#9A7B56]">Manage Addresses</h1>
              <p className="text-xs text-[#8A867E] font-light mt-0.5">Your saved logistics and dispatch profiles</p>
            </div>
          </div>
          <div className="flex items-center gap-3 self-end sm:self-auto">
            <button 
              onClick={openAddModal}
              className="bg-[#F2F1EC] hover:bg-[#9A7B56] text-[#5A5750] hover:text-white border border-[#E1DFD7] text-[11px] font-semibold uppercase tracking-widest px-4 h-9 rounded-sm flex items-center gap-1.5 transition-all duration-300"
            >
              <Plus className="w-3.5 h-3.5 stroke-[2.5]" /> Add Address
            </button>
            <Link to="/" className="text-[11px] text-[#8A867E] font-semibold uppercase tracking-widest h-9 px-3 flex items-center gap-1 hover:text-[#9A7B56] hover:bg-[#FDFDFB] border border-transparent hover:border-[#E1DFD7] rounded-sm transition-all">
              <Home className="w-3.5 h-3.5" /> Home
            </Link>
          </div>
        </div>

        {/* Addresses Grid list display */}
        {addresses.length === 0 ? (
          <div className="text-center py-16 bg-[#FDFDFB] border border-[#E1DFD7] rounded-md max-w-sm mx-auto p-6 space-y-3 shadow-xs">
            <p className="text-[#A6A29A] text-xs font-light uppercase tracking-wider">No saved addresses found.</p>
            <button onClick={openAddModal} className="text-xs text-[#9A7B56] font-semibold uppercase tracking-widest underline">Create one now</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {addresses.map((addr) => (
              <div 
                key={addr.id} 
                className={`bg-[#FDFDFB] border rounded-md p-5 flex flex-col sm:flex-row justify-between items-start gap-4 transition-all hover:shadow-md ${
                  addr.is_default ? 'border-[#9A7B56] shadow-xs' : 'border-[#E1DFD7]'
                }`}
              >
                <div className="space-y-2 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium uppercase tracking-wide text-[#2C2A27]">Delivery Profile</span>
                    {addr.is_default && (
                      <span className="text-[9px] bg-[#9A7B56]/10 text-[#9A7B56] font-bold px-2 py-0.5 rounded-sm flex items-center gap-1 uppercase tracking-widest">
                        <CheckCircle2 className="w-2.5 h-2.5" /> DEFAULT
                      </span>
                    )}
                  </div>
                  
                  <p className="text-xs text-[#5A5750] font-light leading-relaxed">
                    {addr.address_line_1}{addr.address_line_2 ? `, ${addr.address_line_2}` : ''}
                  </p>
                  <p className="text-xs text-[#8A867E]">
                    {addr.city}, {addr.state} - <span className="font-mono text-[#2C2A27] font-medium tracking-wide">{addr.pincode}</span>
                  </p>
                  <p className="text-xs text-[#A6A29A] pt-0.5">
                    <span className="font-medium text-[#8A867E] uppercase tracking-wider text-[10px]">Phone:</span> {addr.phone_number}
                  </p>
                </div>

                {/* Card Actions Panel */}
                <div className="flex items-center gap-1 self-end sm:self-auto border-t sm:border-t-0 pt-3 sm:pt-0 w-full sm:w-auto justify-end">
                  {!addr.is_default && (
                    <button 
                      onClick={() => handleSetDefault(addr.id)}
                      className="text-[10px] text-[#5A5750] hover:text-white hover:bg-[#9A7B56] font-semibold uppercase tracking-widest border border-[#E1DFD7] hover:border-[#9A7B56] px-3 py-1.5 rounded-sm transition-all bg-[#F2F1EC] mr-1"
                    >
                      Set default
                    </button>
                  )}
                  
                  <button 
                    onClick={() => openEditModal(addr)}
                    className="text-[#8A867E] hover:text-[#9A7B56] p-2 rounded-md hover:bg-[#F2F1EC] transition-colors"
                    title="Edit Details"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>

                  <button 
                    onClick={() => handleDelete(addr.id)}
                    className="text-[#8A867E] hover:text-red-700 p-2 rounded-md hover:bg-orange-50/50 transition-colors"
                    title="Delete Address"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>

              </div>
            ))}
          </div>
        )}

        {/* DIALOG MODAL: Handlers for Add/Edit inputs overlay */}
        {isModalOpen && (
          <div className="fixed inset-0 z-50 bg-[#2C2A27]/40 flex items-center justify-center p-4 backdrop-blur-xs">
            <div className="bg-[#FDFDFB] border border-[#E1DFD7] rounded-md max-w-lg w-full overflow-hidden shadow-xl">
              
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-[#E1DFD7] bg-[#F2F1EC]">
                <h2 className="text-[11px] font-bold uppercase tracking-widest text-[#5A5750]">
                  {editingAddressId ? "Modify Address Details" : "Register New Address Layout"}
                </h2>
                <button onClick={() => setIsModalOpen(false)} className="text-[#8A867E] hover:text-[#2C2A27] transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Modal Form content mapping */}
              <form onSubmit={handleFormSubmit} className="p-6 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] font-semibold text-[#8A867E] uppercase tracking-wider mb-1">Contact Phone *</label>
                    <input type="tel" name="phone_number" required value={formData.phone_number} onChange={handleInputChange} className="w-full bg-[#FDFDFB] border border-[#E1DFD7] rounded-sm p-2 text-xs focus:outline-none focus:border-[#9A7B56] transition-colors text-[#2C2A27]" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-[#8A867E] uppercase tracking-wider mb-1">Pincode *</label>
                    <div className="relative">
                      <input type="text" name="pincode" required value={formData.pincode} onChange={handleInputChange} maxLength="6" className="w-full bg-[#FDFDFB] border border-[#E1DFD7] rounded-sm p-2 text-xs focus:outline-none focus:border-[#9A7B56] transition-colors text-[#2C2A27]" placeholder="6-digit PIN" />
                      {pincodeLoading && <span className="absolute right-3 top-2 text-[9px] text-[#9A7B56] animate-pulse font-medium tracking-wide">Syncing...</span>}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-semibold text-[#8A867E] uppercase tracking-wider mb-1">Address Details Flat/House *</label>
                  <input type="text" name="address_line_1" required value={formData.address_line_1} onChange={handleInputChange} className="w-full bg-[#FDFDFB] border border-[#E1DFD7] rounded-sm p-2 text-xs focus:outline-none focus:border-[#9A7B56] transition-colors text-[#2C2A27]" placeholder="Flat number, building block, street metadata" />
                </div>

                <div>
                  <label className="block text-[10px] font-semibold text-[#8A867E] uppercase tracking-wider mb-1">Landmark/Locality (Optional)</label>
                  <input type="text" name="address_line_2" value={formData.address_line_2} onChange={handleInputChange} className="w-full bg-[#FDFDFB] border border-[#E1DFD7] rounded-sm p-2 text-xs focus:outline-none focus:border-[#9A7B56] transition-colors text-[#2C2A27]" placeholder="Near monument, shopping mall etc." />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] font-semibold text-[#8A867E] uppercase tracking-wider mb-1">City *</label>
                    <input type="text" name="city" required value={formData.city} onChange={handleInputChange} className="w-full bg-[#FDFDFB] border border-[#E1DFD7] rounded-sm p-2 text-xs focus:outline-none focus:border-[#9A7B56] transition-colors text-[#2C2A27]" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-[#8A867E] uppercase tracking-wider mb-1">State *</label>
                    <input type="text" name="state" required value={formData.state} onChange={handleInputChange} className="w-full bg-[#FDFDFB] border border-[#E1DFD7] rounded-sm p-2 text-xs focus:outline-none focus:border-[#9A7B56] transition-colors text-[#2C2A27]" />
                  </div>
                </div>

                <label className="flex items-center gap-2 cursor-pointer pt-2 select-none">
                  <input 
                    type="checkbox" 
                    name="is_default"
                    checked={formData.is_default} 
                    onChange={handleInputChange}
                    className="accent-[#9A7B56] rounded-sm h-3.5 w-3.5 border-[#E1DFD7]"
                  />
                  <span className="text-xs text-[#8A867E] font-light">Set this location profile as default delivery shortcut</span>
                </label>

                {/* Form Action Controls */}
                <div className="flex items-center justify-end gap-2 pt-4 border-t border-[#E1DFD7] mt-4">
                  <button 
                    type="button" 
                    onClick={() => setIsModalOpen(false)} 
                    className="px-4 h-9 text-xs font-semibold uppercase tracking-widest text-[#A6A29A] hover:text-[#2C2A27] transition-colors"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="bg-[#F2F1EC] hover:bg-[#9A7B56] text-[#5A5750] hover:text-white border border-[#E1DFD7] text-xs font-semibold uppercase tracking-widest px-5 h-9 rounded-sm transition-all duration-300"
                  >
                    Save Changes
                  </button>
                </div>
              </form>

            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default SavedAddresses;